import { ApplicationEntryValue } from '@/application/components/sheet/ApplicationEntry'
import { ApplicationMediasPreview } from '@/application/components/sheet/ApplicationMediasPreview'
import {
  Application_Media_Type_Enum,
  WebappApplicationFragment,
} from '@gql'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const DOCUMENT_TYPE_ORDER: Application_Media_Type_Enum[] = [
  Application_Media_Type_Enum.Other,
  Application_Media_Type_Enum.SimulationPret,
  Application_Media_Type_Enum.JustificatifApport,
  Application_Media_Type_Enum.AvisImpot,
  Application_Media_Type_Enum.JustificatifRevenus,
]

const DOCUMENT_TYPE_LABEL_KEYS = {
  [Application_Media_Type_Enum.Other]: 'ApplicationSheet.documents',
  [Application_Media_Type_Enum.SimulationPret]:
    'ApplicationSheet.documentsByType.simulationPret',
  [Application_Media_Type_Enum.JustificatifApport]:
    'ApplicationSheet.documentsByType.justificatifApport',
  [Application_Media_Type_Enum.AvisImpot]:
    'ApplicationSheet.documentsByType.avisImpot',
  [Application_Media_Type_Enum.JustificatifRevenus]:
    'ApplicationSheet.documentsByType.justificatifRevenus',
} as const satisfies Record<Application_Media_Type_Enum, string>

type ApplicationForDocumentEntries = Pick<
  WebappApplicationFragment,
  'id' | 'application_medias' | 'promiseOfSalesFileId' | 'authenticActFileId'
>

export type ApplicationDocumentEntry = {
  documentType: Application_Media_Type_Enum
  title: string
  value: React.ReactElement
}

export type ApplicationDocumentEntriesResult = {
  otherDocumentsEntry: { title: string; value: ApplicationEntryValue }
  typedDocumentsEntries: ApplicationDocumentEntry[]
}

export const useApplicationDocumentEntries = (
  application: ApplicationForDocumentEntries
): ApplicationDocumentEntriesResult => {
  const { t } = useTranslation()

  const mediaByType = useMemo(() => {
    const initialMap = DOCUMENT_TYPE_ORDER.reduce(
      (acc, documentType) => ({
        ...acc,
        [documentType]: [] as WebappApplicationFragment['application_medias'],
      }),
      {} as Record<
        Application_Media_Type_Enum,
        WebappApplicationFragment['application_medias']
      >
    )

    return application.application_medias.reduce(
      (acc, applicationMedia) => {
        const documentType =
          applicationMedia.type ?? Application_Media_Type_Enum.Other
        if (documentType in acc) {
          acc[documentType].push(applicationMedia)
          return acc
        }
        acc[Application_Media_Type_Enum.Other].push(applicationMedia)
        return acc
      },
      { ...initialMap }
    )
  }, [application.application_medias])

  const renderDocumentPreviews = useCallback(
    (applicationMedias: WebappApplicationFragment['application_medias']) => (
      <div className="tw-flex tw-flex-wrap tw-gap-2">
        {applicationMedias.map((applicationMedia) => (
          <ApplicationMediasPreview
            key={applicationMedia.id}
            applicationMedia={applicationMedia}
            isImage={applicationMedia.file?.mimeType?.startsWith('image/')}
          />
        ))}
      </div>
    ),
    []
  )

  const documentsByType = useMemo(() => {
    const documentsEntries = DOCUMENT_TYPE_ORDER.map((documentType) => {
      const applicationMedias = mediaByType[documentType]
      if (applicationMedias.length === 0) return null

      return {
        documentType,
        title: t(DOCUMENT_TYPE_LABEL_KEYS[documentType]),
        value: renderDocumentPreviews(applicationMedias),
      }
    })

    return documentsEntries.filter(
      (entry): entry is ApplicationDocumentEntry => entry != null
    )
  }, [mediaByType, renderDocumentPreviews, t])

  const legacyDocuments = useMemo(() => {
    if (!application.promiseOfSalesFileId && !application.authenticActFileId) {
      return null
    }

    return (
      <div className="tw-flex tw-flex-wrap tw-gap-2">
        {application.promiseOfSalesFileId && (
          <ApplicationMediasPreview
            key={application.promiseOfSalesFileId}
            applicationMedia={{
              id: application.promiseOfSalesFileId,
              name: t('ApplicationSheet.promiseOfSale.title'),
              fileId: application.promiseOfSalesFileId,
              applicationId: application.id,
            }}
            isImage={false}
          />
        )}
        {application.authenticActFileId && (
          <ApplicationMediasPreview
            key={application.authenticActFileId}
            applicationMedia={{
              id: application.authenticActFileId,
              name: t('ApplicationSheet.authenticAct.title'),
              fileId: application.authenticActFileId,
              applicationId: application.id,
            }}
            isImage={false}
          />
        )}
      </div>
    )
  }, [
    application.authenticActFileId,
    application.id,
    application.promiseOfSalesFileId,
    t,
  ])

  const otherDocumentsEntry = useMemo((): {
    title: string
    value: ApplicationEntryValue
  } => {
    const otherDocuments = documentsByType.find(
      (entry) => entry.documentType === Application_Media_Type_Enum.Other
    )

    if (!otherDocuments && !legacyDocuments) {
      return {
        title: t('ApplicationSheet.documents'),
        value: t('ApplicationSheet.notDefined'),
      }
    }

    return {
      title: t('ApplicationSheet.documents'),
      value: (
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {otherDocuments?.value}
          {legacyDocuments}
        </div>
      ),
    }
  }, [documentsByType, legacyDocuments, t])

  const typedDocumentsEntries = useMemo(
    () =>
      documentsByType.filter(
        (entry) => entry.documentType !== Application_Media_Type_Enum.Other
      ),
    [documentsByType]
  )

  return { otherDocumentsEntry, typedDocumentsEntries }
}
